import React from "react";
import { Checkbox, Table } from "evergreen-ui";
import { ColumnConfig } from "../../../../common/tables/config/ColumnConfig";
import { EnumResolver } from "../../../../common/tables/etc/EnumResolver";
import { EnumMapper, ResolvedEnum } from "../../../../common/tables/etc/EnumMapper";
import { TableCellChangedHandler, TableCellFactory } from "../../../../common/tables/factory";
import { CostMetadataTableRow } from "../../../../../../common/tables/cost-metadata/CostMetadataTableRow";
import { CostMetadata, CostPayoutModifier, ShopifyProductVariant } from "../../../../../../common/models";

const emptyMapper: EnumMapper = {
    map(): ResolvedEnum {
        return {};
    }
};

class CostMetadataAssignmentEnumMapper extends EnumResolver<CostMetadataTableRow> {
    public resolveEnum(_columnName: keyof CostMetadataTableRow): EnumMapper {
        return emptyMapper;
    }
}

type CostMetadataWithFlags = CostMetadata & {
    _assigned: boolean;
    _lastVariant: boolean;
    shopifyProductVariant?: ShopifyProductVariant[];
    costPayoutModifier?: CostPayoutModifier | null;
};

const DOLLAR_COLUMNS: Set<string> = new Set([
    "unitCost", "juniperServiceFee", "fulfillmentFee", "shippingSubsidy",
]);

export class CostMetadataAssignmentTableCellFactory extends TableCellFactory<CostMetadataTableRow, CostMetadataWithFlags> {
    readonly enumResolver: EnumResolver<CostMetadataTableRow> = new CostMetadataAssignmentEnumMapper();

    public renderBodyCell(
        columnConfig: ColumnConfig<CostMetadataTableRow>,
        rowData: CostMetadataWithFlags,
        onCellChanged: TableCellChangedHandler<CostMetadataTableRow>,
    ): JSX.Element {
        if (columnConfig.columnName === "hasAllVariantsCovered") {
            const isDisabled = rowData._assigned && rowData._lastVariant;
            return React.createElement(
                Table.Cell,
                { className: "jc-body-cell checkbox" },
                React.createElement(Checkbox, {
                    checked: rowData._assigned,
                    disabled: isDisabled,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        if (onCellChanged) {
                            onCellChanged(
                                rowData.id,
                                columnConfig.columnName as keyof CostMetadataTableRow,
                                e.target.checked,
                            );
                        }
                    },
                }),
            );
        }

        return super.renderBodyCell(columnConfig, rowData, onCellChanged);
    }

    protected resolveCellData(
        columnConfig: ColumnConfig<CostMetadataTableRow>,
        rowData: CostMetadataWithFlags,
    ): string {
        const colName = columnConfig.columnName as string;

        if (colName === "hasAllVariantsCovered") {
            return rowData._assigned ? "true" : "";
        }

        if (colName === "variantSkus") {
            const variants = rowData.shopifyProductVariant ?? [];
            if (variants.length === 0) return "";
            const first = variants[0].sku || variants[0].title || String(variants[0].id);
            if (variants.length === 1) return first;
            return `${first}, +${variants.length - 1}`;
        }

        if (colName === "costPayoutModifierName") {
            return rowData.costPayoutModifier?.name ?? "";
        }

        if (DOLLAR_COLUMNS.has(colName) || colName === "processingFeePercent") {
            const raw = (rowData as unknown as Record<string, unknown>)[colName];
            const num = Number(raw);
            if (isNaN(num)) return String(raw ?? "");
            return num.toFixed(2);
        }

        return super.resolveCellData(columnConfig, rowData);
    }
}
